import { ENDPOINTS } from '__constants__'
import { auth } from 'services/firebase'
import { message } from 'antd'
import { useConfirmation } from 'domains/Confirmation/context'
import { useSignInWithProviderCredentials } from 'domains/Session/hooks'
import PATHS from 'pages/paths'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { signInWithCustomToken } from 'firebase/auth'
import { useTranslations } from 'contexts/Translation'

const hasVippsCallbackParams = (search) => {
  const params = new URLSearchParams(search)

  return params.has('code') || params.has('error')
}

const useVippsLogin = () => {
  const location = useLocation()
  const isVippsCallbackRoute =
    location.pathname === PATHS.UNAUTHENTICATED.VIPPS_CALLBACK

  const [loading, setLoading] = useState(() => {
    if (typeof window === 'undefined') {
      return false
    }

    return (
      window.location.pathname === PATHS.UNAUTHENTICATED.VIPPS_CALLBACK &&
      hasVippsCallbackParams(window.location.search)
    )
  })
  const { setIsUserFinishedConfirmation } = useConfirmation()
  const signInWithProviderCredentials = useSignInWithProviderCredentials()
  const { t } = useTranslations()
  const history = useHistory()
  const hasStartedCallback = useRef(false)

  const completeVippsLogin = useCallback(
    async ({ code, state, error, errorDescription }) => {
      try {
        setIsUserFinishedConfirmation(false)
        setLoading(true)

        if (error) {
          throw new Error(errorDescription || error)
        }

        if (!code || !state) {
          throw new Error(t('Error during Vipps login'))
        }

        const response = await fetch(ENDPOINTS.VIPPS_AUTH_COMPLETE, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ code, state })
        })

        const responseData = await response.json()

        if (!response.ok) {
          throw new Error(
            responseData?.message ||
              responseData?.error ||
              t('Error during Vipps login')
          )
        }

        const customToken = responseData?.data?.customToken

        if (!customToken) {
          throw new Error(t('Error during Vipps login'))
        }

        const credentials = await signInWithCustomToken(auth, customToken)

        await signInWithProviderCredentials(credentials)
      } catch (loginError) {
        message.error(
          `${t('Error during Vipps login')}${
            loginError?.message ? `, ${loginError.message}` : ''
          }`
        )
        history.replace(PATHS.UNAUTHENTICATED.LOGIN)
      } finally {
        setIsUserFinishedConfirmation(true)
        setLoading(false)
      }
    },
    [history, setIsUserFinishedConfirmation, signInWithProviderCredentials, t]
  )

  useEffect(() => {
    if (!isVippsCallbackRoute || hasStartedCallback.current) {
      return
    }

    hasStartedCallback.current = true

    const params = new URLSearchParams(location.search)

    completeVippsLogin({
      code: params.get('code'),
      state: params.get('state'),
      error: params.get('error'),
      errorDescription: params.get('error_description')
    })
  }, [completeVippsLogin, isVippsCallbackRoute, location.search])

  const vippsLogin = async () => {
    try {
      setIsUserFinishedConfirmation(false)
      setLoading(true)

      const response = await fetch(ENDPOINTS.VIPPS_AUTH_START, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          redirectUri: `${window.location.origin}${PATHS.UNAUTHENTICATED.VIPPS_CALLBACK}`
        })
      })

      const responseData = await response.json()

      if (!response.ok) {
        throw new Error(
          responseData?.message ||
            responseData?.error ||
            t('Error during Vipps login')
        )
      }

      const authorizationUrl = responseData?.data?.authorizationUrl

      if (!authorizationUrl) {
        throw new Error(t('Error during Vipps login'))
      }

      window.location.assign(authorizationUrl)
    } catch (error) {
      message.error(
        `${t('Error during Vipps login')}${
          error?.message ? `, ${error.message}` : ''
        }`
      )
      setIsUserFinishedConfirmation(true)
      setLoading(false)
    }
  }

  return { vippsLogin, loading }
}

export default useVippsLogin
